import { useEffect, useState } from "react";
import { http, API } from "../api";
import { Landmark, Plus, Trash2, Pencil, Eye, EyeOff, UserPlus, Video, Image as ImageIcon, X, ExternalLink, Search } from "lucide-react";
import { Modal, PageHeader, EmptyState, FormRow, purgeDraft } from "../shared";
import PhotoUpload from "./PhotoUpload";

const empty = {
  name: "", government: "central", category: "General", description: "", benefit: "",
  docs_required: [], process_steps: [], hero_file_id: "", media: [],
  assigned_user_ids: [], linked_form_id: "", is_hidden: false,
};
const STATUSES = ["Approved", "Rejected", "Past Beneficiary", "Under Inquiry"];
const STATUS_TONE = { Approved: "green", Rejected: "red", "Past Beneficiary": "blue", "Under Inquiry": "amber" };

export default function Schemes({ user }) {
  const isAdmin = user && (user.role === "Super Admin" || user.role === "Admin");
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [forms, setForms] = useState([]);
  const [govFilter, setGovFilter] = useState("");
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [detailFor, setDetailFor] = useState(null);
  const [detail, setDetail] = useState(null);
  const [benefs, setBenefs] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [candidates, setCandidates] = useState([]);

  const load = () => http.get(`/schemes${govFilter ? "?government=" + govFilter : ""}`).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [govFilter]);
  useEffect(() => {
    if (isAdmin) http.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    http.get("/forms").then((r) => setForms(r.data)).catch(() => {});
  }, [isAdmin]);

  const openNew = () => { setEditing(null); setForm(empty); setShow(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...empty, ...s }); setShow(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await http.patch(`/schemes/${editing.scheme_id}`, form);
      else await http.post("/schemes", form);
      purgeDraft(editing ? `scheme-${editing.scheme_id}` : "scheme-new");
      setShow(false); setEditing(null); setForm(empty); load();
    } catch (e) {
      alert(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const del = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try { await http.delete(`/schemes/${s.scheme_id}`); load(); }
    catch (e) { alert(e.response?.data?.detail || "Delete failed"); }
  };

  const toggleHide = async (s) => {
    try { await http.patch(`/schemes/${s.scheme_id}`, { is_hidden: !s.is_hidden }); load(); }
    catch (e) { alert(e.response?.data?.detail || "Update failed"); }
  };

  const openDetail = async (s) => {
    setDetailFor(s); setBenefs([]); setDetail(null);
    try {
      const r = await http.get(`/schemes/${s.scheme_id}`);
      setDetail(r.data);
      const b = await http.get(`/schemes/${s.scheme_id}/beneficiaries`);
      setBenefs(b.data);
    } catch (e) { alert(e.response?.data?.detail || "Failed to load scheme"); }
  };
  useEffect(() => {
    if (!searchQ || searchQ.length < 2) { setCandidates([]); return; }
    const t = setTimeout(() => http.get(`/contacts?q=${encodeURIComponent(searchQ)}`).then((r) => setCandidates(r.data.slice(0, 8))), 250);
    return () => clearTimeout(t);
  }, [searchQ]);

  const addBenef = async (c, status = "Under Inquiry") => {
    try {
      await http.post(`/schemes/${detailFor.scheme_id}/beneficiaries`, { scheme_id: detailFor.scheme_id, contact_id: c.contact_id, status });
      const b = await http.get(`/schemes/${detailFor.scheme_id}/beneficiaries`);
      setBenefs(b.data); setSearchQ(""); setCandidates([]);
      load();
    } catch (e) { alert(e.response?.data?.detail || "Failed to tag beneficiary"); }
  };
  const updateBenef = async (bnf, patch) => {
    try {
      await http.patch(`/schemes/${detailFor.scheme_id}/beneficiaries/${bnf.beneficiary_id}`, patch);
      const b = await http.get(`/schemes/${detailFor.scheme_id}/beneficiaries`);
      setBenefs(b.data);
    } catch (e) { alert(e.response?.data?.detail || "Update failed"); }
  };
  const removeBenef = async (bnf) => {
    if (!window.confirm(`Remove ${bnf.contact_name}?`)) return;
    try {
      await http.delete(`/schemes/${detailFor.scheme_id}/beneficiaries/${bnf.beneficiary_id}`);
      const b = await http.get(`/schemes/${detailFor.scheme_id}/beneficiaries`);
      setBenefs(b.data); load();
    } catch (e) { alert(e.response?.data?.detail || "Remove failed"); }
  };

  return (
    <>
      <PageHeader eyebrow="ENTITLEMENTS" title="Government Schemes"
        subtitle={`${rows.length} schemes · Central & West Bengal`}
        actions={<>
          <select data-testid="scheme-gov-filter" value={govFilter} onChange={(e) => setGovFilter(e.target.value)}>
            <option value="">All governments</option>
            <option value="central">Central</option>
            <option value="wb">West Bengal</option>
          </select>
          {isAdmin && <button className="primary" data-testid="scheme-add-button" onClick={openNew}><Plus size={16} /> Add scheme</button>}
        </>} />

      <section className="panel">
        {rows.length === 0 ? <EmptyState icon={Landmark} title="No schemes yet" hint="Add a central or state scheme to begin." /> : (
          <div className="grid-cards">
            {rows.map((s, i) => (
              <div key={s.scheme_id} className="org-card" data-testid={`scheme-card-${i}`} style={s.is_hidden ? { opacity: 0.6, borderStyle: "dashed" } : {}}>
                {s.hero_file_id && <div style={{ height: 100, marginBottom: 8, borderRadius: 8, background: `#eee center/cover no-repeat url(${API}/files/${s.hero_file_id})` }} />}
                <div className="org-head">
                  <Landmark size={18} /><b>{s.name}</b>
                  <span className={`chip ${s.government === "central" ? "blue" : "green"}`}>{s.government === "central" ? "Central" : "WB State"}</span>
                  {s.is_hidden && <span className="chip amber">Hidden</span>}
                </div>
                <p className="hint">{s.category} · {s.benefit || "—"}</p>
                <p>{(s.description || "").slice(0, 140)}{(s.description || "").length > 140 ? "…" : ""}</p>
                <p className="hint"><b>{s.beneficiary_count || 0}</b> beneficiaries · <b>{(s.assigned_user_ids || []).length}</b> assigned</p>
                <div className="row-actions" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                  <button className="link" data-testid={`scheme-open-${i}`} onClick={() => openDetail(s)}><Eye size={12} /> Open</button>
                  {isAdmin && (
                    <>
                      <button className="link" data-testid={`scheme-edit-${i}`} onClick={() => openEdit(s)}><Pencil size={12} /> Edit</button>
                      <button className="link" data-testid={`scheme-hide-${i}`} onClick={() => toggleHide(s)}>{s.is_hidden ? <><Eye size={12} /> Unhide</> : <><EyeOff size={12} /> Hide</>}</button>
                      <button className="link danger" data-testid={`scheme-delete-${i}`} onClick={() => del(s)}><Trash2 size={12} /> Delete</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {show && (
        <Modal
          title={editing ? `Edit ${editing.name}` : "Add scheme"}
          onClose={() => { setShow(false); setEditing(null); }}
          testid="scheme-modal"
          autosaveKey={editing ? `scheme-${editing.scheme_id}` : "scheme-new"}
          getState={() => form}
          applyState={(s) => setForm({ ...empty, ...(s || {}) })}
          isDirty={() => JSON.stringify(form) !== JSON.stringify(editing ? { ...empty, ...editing } : empty)}>
          <div className="form-grid">
            <FormRow label="Name"><input data-testid="sf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormRow>
            <FormRow label="Government">
              <select data-testid="sf-gov" value={form.government} onChange={(e) => setForm({ ...form, government: e.target.value })}>
                <option value="central">Central</option><option value="wb">West Bengal</option>
              </select>
            </FormRow>
            <FormRow label="Category"><input data-testid="sf-cat" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></FormRow>
            <FormRow label="Benefit summary"><input data-testid="sf-benefit" value={form.benefit} onChange={(e) => setForm({ ...form, benefit: e.target.value })} placeholder="₹5,00,000 / year" /></FormRow>
          </div>
          <FormRow label="Description"><textarea data-testid="sf-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormRow>
          <FormRow label="Documents required (comma separated)"><input data-testid="sf-docs" value={(form.docs_required || []).join(", ")} onChange={(e) => setForm({ ...form, docs_required: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} placeholder="Aadhaar, Bank passbook, …" /></FormRow>
          <FormRow label="Process steps (one per line)"><textarea data-testid="sf-steps" rows={4} value={(form.process_steps || []).join("\n")} onChange={(e) => setForm({ ...form, process_steps: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })} /></FormRow>
          <div style={{ display: "flex", gap: 20, alignItems: "flex-start", marginTop: 10, flexWrap: "wrap" }}>
            <PhotoUpload category="scheme" value={form.hero_file_id} onChange={(v) => setForm({ ...form, hero_file_id: v })} shape="banner" label="Hero image" testid="scheme-hero" />
          </div>
          <FormRow label="YouTube / Vimeo URL (optional)"><input data-testid="sf-video" value={(form.media || []).find((m) => m.kind === "video_url")?.url || ""} onChange={(e) => setForm({ ...form, media: [{ kind: "video_url", url: e.target.value }, ...(form.media || []).filter((m) => m.kind !== "video_url")] })} placeholder="https://youtu.be/…" /></FormRow>
          <FormRow label="Native video (MP4/WebM up to 100MB)">
            <SchemeVideoUpload
              current={(form.media || []).find((m) => m.kind === "video_file")}
              onSet={(fileId) => setForm({
                ...form,
                media: [
                  ...(form.media || []).filter((m) => m.kind !== "video_file"),
                  ...(fileId ? [{ kind: "video_file", file_id: fileId }] : []),
                ],
              })}
            />
          </FormRow>
          <FormRow label="Linked application form">
            <select data-testid="sf-form" value={form.linked_form_id || ""} onChange={(e) => setForm({ ...form, linked_form_id: e.target.value })}>
              <option value="">— None —</option>
              {forms.map((f) => <option key={f.form_id} value={f.form_id}>{f.title}</option>)}
            </select>
          </FormRow>
          <FormRow label="Assigned admins / members">
            <select multiple data-testid="sf-assigned" value={form.assigned_user_ids} onChange={(e) => setForm({ ...form, assigned_user_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })} style={{ minHeight: 100 }}>
              {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name} — {u.role}</option>)}
            </select>
          </FormRow>
          <div className="form-actions">
            <button className="outline" data-testid="sf-cancel" onClick={() => { purgeDraft(editing ? `scheme-${editing.scheme_id}` : "scheme-new"); setShow(false); setEditing(null); }}>Cancel</button>
            <button className="primary" data-testid="sf-save" disabled={saving || !form.name} onClick={save}>{saving ? "Saving…" : editing ? "Save" : "Create scheme"}</button>
          </div>
        </Modal>
      )}

      {detailFor && (
        <Modal title={detailFor.name} onClose={() => setDetailFor(null)} testid="scheme-detail-modal">
          {!detail ? <p className="hint">Loading…</p> : (
            <>
              {detail.hero_file_id && <img src={`${API}/files/${detail.hero_file_id}`} alt="hero" style={{ width: "100%", borderRadius: 10, marginBottom: 12 }} />}
              <span className={`chip ${detail.government === "central" ? "blue" : "green"}`}>{detail.government === "central" ? "Central Government" : "West Bengal Government"}</span>
              <span className="chip amber" style={{ marginLeft: 6 }}>{detail.category}</span>
              <p style={{ marginTop: 12 }}>{detail.description}</p>
              <p><b>Benefit: </b>{detail.benefit || "—"}</p>

              {(detail.docs_required || []).length > 0 && (
                <>
                  <p className="eyebrow" style={{ marginTop: 12 }}>DOCUMENTS REQUIRED</p>
                  <ul>{detail.docs_required.map((d, i) => <li key={i}>{d}</li>)}</ul>
                </>
              )}
              {(detail.process_steps || []).length > 0 && (
                <>
                  <p className="eyebrow" style={{ marginTop: 12 }}>PROCESS</p>
                  <ol>{detail.process_steps.map((d, i) => <li key={i}>{d}</li>)}</ol>
                </>
              )}
              {(detail.media || []).filter((m) => m.kind === "video_file" && m.file_id).map((m, i) => (
                <div key={"vf" + i} style={{ marginTop: 12 }} data-testid={`scheme-video-file-${i}`}>
                  <p className="eyebrow">VIDEO</p>
                  <video controls preload="metadata" style={{ width: "100%", maxHeight: 360, borderRadius: 10, background: "#000" }}>
                    <source src={`${API}/files/${m.file_id}`} />
                  </video>
                </div>
              ))}
              {(detail.media || []).filter((m) => m.kind === "video_url" && m.url).map((m, i) => {
                const vid = (m.url.match(/[?&]v=([^&]+)/) || m.url.match(/youtu\.be\/([^?&]+)/) || [])[1];
                return vid ? (
                  <div key={i} style={{ marginTop: 12 }}>
                    <p className="eyebrow">VIDEO</p>
                    <iframe title="scheme-video" width="100%" height="260" src={`https://www.youtube.com/embed/${vid}`} frameBorder="0" allow="autoplay; encrypted-media" allowFullScreen />
                  </div>
                ) : <a key={i} href={m.url} target="_blank" rel="noreferrer" className="link" style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8 }}><Video size={14} /> Watch <ExternalLink size={11} /></a>;
              })}

              {(detail.assigned || []).length > 0 && (
                <>
                  <p className="eyebrow" style={{ marginTop: 12 }}>SCHEME OWNERS</p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-testid="scheme-owners">
                    {detail.assigned.map((a) => (
                      <div key={a.user_id} className="notice-strip" style={{ background: "#f7fbf8", color: "var(--ink)", borderColor: "var(--line)" }} data-testid={`owner-${a.user_id}`}>
                        <b>{a.name}</b> · {a.role} · {a.mobile || "no mobile"} {a.whatsapp ? <>· WA {a.whatsapp}</> : null}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {detail.linked_form_id && (
                <p className="hint" style={{ marginTop: 12 }} data-testid="scheme-linked-form">
                  Application form: <b>{forms.find((f) => f.form_id === detail.linked_form_id)?.title || detail.linked_form_id}</b>
                </p>
              )}

              <div style={{ background: "#fff", border: "1px solid var(--line)", borderRadius: 10, padding: 12, marginTop: 12 }}>
                <p className="eyebrow" style={{ margin: 0 }}>BENEFICIARIES ({benefs.length})</p>
                {isAdmin && (
                  <div style={{ position: "relative", marginTop: 8 }}>
                    <span style={{ position: "absolute", left: 10, top: 10, opacity: 0.6 }}><Search size={14} /></span>
                    <input placeholder="Tag a resident…" value={searchQ} onChange={(e) => setSearchQ(e.target.value)} data-testid="benef-search-input" style={{ width: "100%", paddingLeft: 30 }} />
                    {candidates.length > 0 && (
                      <div className="people-table" style={{ marginTop: 6, background: "#fff", border: "1px solid var(--line)", borderRadius: 8, maxHeight: 200, overflowY: "auto" }} data-testid="benef-candidates">
                        {candidates.map((c, i) => (
                          <div key={c.contact_id} className="tr" style={{ padding: "6px 10px" }}>
                            <span><b>{c.name}</b> <small>· {c.mobile || ""} · Booth {c.booth || ""}</small></span>
                            <button className="primary" data-testid={`benef-add-${i}`} onClick={() => addBenef(c)} style={{ padding: "6px 10px" }}><UserPlus size={12} /> Tag</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {benefs.length > 0 && (
                  <div className="people-table" style={{ marginTop: 10 }} data-testid="benef-list">
                    <div className="tr th"><span>Name</span><span>Status</span><span>Remark</span><span></span></div>
                    {benefs.map((b, i) => (
                      <div key={b.beneficiary_id} className="tr" data-testid={`benef-row-${i}`}>
                        <span><b>{b.contact_name}</b><small> {b.contact_mobile || ""}</small></span>
                        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span className={`chip ${STATUS_TONE[b.status] || "blue"}`} data-testid={`benef-status-chip-${i}`}>{b.status}</span>
                          {isAdmin && (
                            <select value={b.status} data-testid={`benef-status-${i}`} onChange={(e) => updateBenef(b, { status: e.target.value })} style={{ fontSize: 11 }}>
                              {STATUSES.map((s) => <option key={s}>{s}</option>)}
                            </select>
                          )}
                        </span>
                        <span>{isAdmin ? <input defaultValue={b.remark || ""} data-testid={`benef-remark-${i}`} onBlur={(e) => { if (e.target.value !== b.remark) updateBenef(b, { remark: e.target.value }); }} /> : <small>{b.remark || "—"}</small>}</span>
                        <span>{isAdmin && <button className="link danger" data-testid={`benef-remove-${i}`} onClick={() => removeBenef(b)}><X size={12} /></button>}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </Modal>
      )}
    </>
  );
}

function SchemeVideoUpload({ current, onSet }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const url = current?.file_id ? `${API}/files/${current.file_id}` : "";
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await http.post("/uploads/scheme", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
      onSet(r.data.file_id);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {url ? (
        <video controls preload="metadata" style={{ width: "100%", maxHeight: 240, borderRadius: 8, background: "#000" }} data-testid="scheme-video-preview">
          <source src={url} />
        </video>
      ) : (
        <p className="hint" style={{ margin: 0 }}>No native video attached. Add an MP4/WebM up to 100MB.</p>
      )}
      <div style={{ display: "flex", gap: 10 }}>
        <label className="outline" style={{ cursor: "pointer" }} data-testid="scheme-video-upload">
          {busy ? "Uploading…" : (url ? "Replace video" : "Upload video")}
          <input type="file" accept="video/mp4,video/webm,video/quicktime" style={{ display: "none" }} onChange={handle} disabled={busy} data-testid="scheme-video-input" />
        </label>
        {url && (
          <button type="button" className="link danger" onClick={() => onSet(null)} data-testid="scheme-video-clear">Remove</button>
        )}
      </div>
      {err && <small style={{ color: "#b04a3f" }}>{err}</small>}
    </div>
  );
}
